import { useState, useMemo, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  moderators,
  recentActions,
  bannedUsers,
  modReports,
  auditLog,
  moderatorRoleLabels,
  moderatorRoleIcons,
  moderatorRoleColors,
  modActionConfig,
  moderatorDashboardStats,
  moderatorRules,
  getModeratorsByRole,
  getOnlineModerators,
  getRecentActionsByAction,
  getReportsByStatus,
  getAuditLogByAction,
  getActionStats,
  type ModeratorRole,
  type ModAction,
  type ModReport,
  type ModeratorActionRecord,
  type AuditLogEntry,
} from '@/mocks/rfwModerators';

type PageState = 'loading' | 'error' | 'ready';
type ViewSection = 'moderadores' | 'acciones' | 'reportes' | 'bloqueos' | 'auditoria';

export default function ModeradoresPage() {
  const [pageState, setPageState] = useState<PageState>('loading');
  const [activeView, setActiveView] = useState<ViewSection>('moderadores');
  const [roleFilter, setRoleFilter] = useState<ModeratorRole | 'todas'>('todas');
  const [actionFilter, setActionFilter] = useState<ModAction | 'todas'>('todas');
  const [reportFilter, setReportFilter] = useState<ModReport['status'] | 'todas'>('todas');
  const [auditActionFilter, setAuditActionFilter] = useState<ModAction | 'todas'>('todas');
  const [selectedMod, setSelectedMod] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setPageState('ready'), 400);
    return () => clearTimeout(t);
  }, []);

  const handleRetry = useCallback(() => {
    setPageState('loading');
    setTimeout(() => setPageState('ready'), 400);
  }, []);

  const onlineMods = getOnlineModerators();
  const filteredMods = getModeratorsByRole(roleFilter);
  const filteredActions = getRecentActionsByAction(actionFilter);
  const filteredReports = getReportsByStatus(reportFilter);
  const filteredAudit = getAuditLogByAction(auditActionFilter);
  const actionStats = getActionStats();

  const totalActionsAll = actionStats.reduce((s, a) => s + a.count, 0);

  const views: { id: ViewSection; label: string; icon: string; badge?: number }[] = [
    { id: 'moderadores', label: 'Moderadores', icon: 'ri-shield-user-line', badge: moderators.length },
    { id: 'acciones', label: 'Acciones', icon: 'ri-flashlight-line', badge: recentActions.length },
    { id: 'reportes', label: 'Reportes', icon: 'ri-alert-line', badge: modReports.filter((r) => r.status === 'pendiente').length },
    { id: 'bloqueos', label: 'Bloqueos', icon: 'ri-forbid-line', badge: bannedUsers.length },
    { id: 'auditoria', label: 'Auditoría', icon: 'ri-file-search-line', badge: auditLog.length },
  ];

  if (pageState === 'loading') {
    return (
      <div className="min-h-screen bg-background-50">
        <section className="relative w-full h-[200px] md:h-[260px] bg-background-200 animate-pulse" />
        <main className="max-w-[1280px] mx-auto px-4 md:px-6 py-8 space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-1 bg-background-100 rounded-2xl h-[600px] animate-pulse" />
            <div className="md:col-span-2 bg-background-100 rounded-2xl h-[600px] animate-pulse" />
          </div>
        </main>
      </div>
    );
  }

  if (pageState === 'error') {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col items-center justify-center px-4 py-20">
        <div className="w-20 h-20 rounded-3xl bg-red-100 flex items-center justify-center mb-5">
          <i className="ri-error-warning-line text-3xl text-red-500"></i>
        </div>
        <h2 className="text-xl font-extrabold text-foreground-950 mb-2">Error al cargar moderadores</h2>
        <p className="text-sm text-foreground-500 mb-6 text-center max-w-md">No pudimos cargar los datos del sistema de moderación. Verificá tu conexión e intentá de nuevo.</p>
        <button onClick={handleRetry} className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-xl text-sm font-bold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
          <i className="ri-refresh-line"></i>Reintentar
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      {/* HERO */}
      <section className="relative w-full h-[220px] md:h-[320px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Professional%20community%20moderation%20dashboard%20concept%20abstract%20geometric%20security%20shield%20patterns%20floating%20translucent%20in%20organized%20layout%20warm%20ambient%20lighting%20rose%20amber%20tones%20editorial%20product%20photography%20clean%20modern%20aesthetic&width=1600&height=900&seq=rfw-moderadores-hero&orientation=landscape"
          alt="Sistema de Moderadores RFW"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/70"></div>
        <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center">
          <div className="w-16 h-16 rounded-2xl bg-background-50/15 backdrop-blur-md flex items-center justify-center mb-4 border border-background-50/20">
            <i className="ri-shield-user-line text-3xl text-background-50"></i>
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold text-background-50 leading-tight mb-3">
            Sistema de Moderadores
          </h1>
          <p className="text-sm md:text-base text-background-50/80 max-w-xl leading-relaxed">
            Comunidades sanas, moderación transparente. Cada decisión, cada acción y cada reporte quedan registrados para siempre. La confianza se construye con transparencia.
          </p>
        </div>
      </section>

      {/* MAIN */}
      <main className="max-w-[1280px] mx-auto px-4 md:px-6 py-6 md:py-8">
        {/* LIVE PULSE BAR */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 mb-6">
          {[
            { label: 'Moderadores', value: moderatorDashboardStats.totalModerators.toLocaleString(), icon: 'ri-shield-user-line', color: 'text-rose-600 bg-rose-50' },
            { label: 'En Línea', value: moderatorDashboardStats.moderatorsOnline.toString(), icon: 'ri-user-smile-line', color: 'text-emerald-600 bg-emerald-50' },
            { label: 'Acciones Hoy', value: (moderatorDashboardStats.actionsToday / 1000).toFixed(1) + 'K', icon: 'ri-flashlight-line', color: 'text-amber-600 bg-amber-50' },
            { label: 'Bloqueos Activos', value: (moderatorDashboardStats.activeBlocks / 1000).toFixed(1) + 'K', icon: 'ri-forbid-line', color: 'text-red-600 bg-red-50' },
            { label: 'T. Respuesta', value: moderatorDashboardStats.avgResponseTime, icon: 'ri-timer-line', color: 'text-sky-600 bg-sky-50' },
            { label: 'Auditoría', value: (moderatorDashboardStats.totalAuditEntries / 1000000).toFixed(1) + 'M', icon: 'ri-file-search-line', color: 'text-slate-600 bg-slate-50' },
          ].map((stat, i) => (
            <div key={i} className="flex items-center gap-2.5 p-3 rounded-xl bg-background-50 border border-background-200/70">
              <div className={`w-8 h-8 rounded-full ${stat.color} flex items-center justify-center shrink-0`}>
                <i className={`${stat.icon} text-xs`}></i>
              </div>
              <div>
                <p className="text-sm font-extrabold text-foreground-950">{stat.value}</p>
                <p className="text-[9px] text-foreground-500">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
          {/* SIDEBAR */}
          <aside className="md:col-span-3">
            <div className="rounded-2xl bg-background-50 border border-background-200/70 overflow-hidden sticky top-4">
              <div className="p-4 border-b border-background-200/50">
                <h2 className="text-sm font-extrabold text-foreground-950 mb-1">Panel de Moderación</h2>
                <p className="text-[10px] text-foreground-500">Gestioná la comunidad RFW</p>
              </div>

              <nav className="p-2">
                {views.map((v) => (
                  <button
                    key={v.id}
                    onClick={() => setActiveView(v.id)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-left transition-all duration-200 cursor-pointer mb-1 ${
                      activeView === v.id
                        ? 'bg-rose-50 text-rose-700 font-bold'
                        : 'text-foreground-600 hover:bg-background-100'
                    }`}
                  >
                    <i className={`${v.icon} text-sm ${activeView === v.id ? 'text-rose-600' : ''}`}></i>
                    <span className="text-xs flex-1">{v.label}</span>
                    {v.badge !== undefined && v.badge > 0 && (
                      <span className={`w-5 h-5 rounded-full text-[9px] font-bold flex items-center justify-center ${
                        activeView === v.id ? 'bg-rose-200 text-rose-700' : 'bg-background-100 text-foreground-500'
                      }`}>
                        {v.badge}
                      </span>
                    )}
                  </button>
                ))}
              </nav>

              <div className="p-3 mx-2 mb-2 rounded-xl bg-background-100">
                <p className="text-[10px] font-bold text-foreground-600 mb-2">Moderadores en Línea</p>
                <div className="flex flex-wrap gap-1.5">
                  {onlineMods.slice(0, 6).map((m) => (
                    <div key={m.id} className="relative w-8 h-8" title={m.name}>
                      <img src={m.photoUrl} alt={m.name} className="w-8 h-8 rounded-full object-cover" />
                      <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-500 border-2 border-background-50"></span>
                    </div>
                  ))}
                  {onlineMods.length > 6 && (
                    <span className="w-8 h-8 rounded-full bg-background-200 flex items-center justify-center text-[10px] font-bold text-foreground-500">
                      +{onlineMods.length - 6}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </aside>

          {/* MAIN CONTENT */}
          <div className="md:col-span-9">
            {/* VIEW: MODERADORES */}
            {activeView === 'moderadores' && (
              <div className="space-y-5">
                <div className="flex flex-wrap items-center gap-1.5">
                  {([{ id: 'todas' as const, label: 'Todos los Roles', icon: 'ri-group-line' }] as { id: ModeratorRole | 'todas'; label: string; icon: string }[]).concat(
                    (['radio', 'programa', 'club', 'evento'] as ModeratorRole[]).map((r) => ({
                      id: r,
                      label: moderatorRoleLabels[r],
                      icon: moderatorRoleIcons[r],
                    }))
                  ).map((f) => (
                    <button
                      key={f.id}
                      onClick={() => setRoleFilter(f.id)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium transition-all duration-200 whitespace-nowrap cursor-pointer border ${
                        roleFilter === f.id
                          ? 'bg-primary-500 text-white border-primary-500'
                          : 'bg-background-50 text-foreground-500 border-background-200 hover:border-foreground-300'
                      }`}
                    >
                      <i className={`${f.icon} text-[9px]`}></i>
                      {f.label}
                    </button>
                  ))}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredMods.map((mod) => {
                    const roleColors = moderatorRoleColors[mod.role];
                    return (
                      <div
                        key={mod.id}
                        className={`p-4 rounded-2xl border transition-all duration-200 cursor-pointer ${
                          selectedMod === mod.id
                            ? 'bg-rose-50 border-rose-300/60 shadow-sm'
                            : 'bg-background-50 border-background-200/70 hover:border-rose-300/60'
                        }`}
                        onClick={() => setSelectedMod(selectedMod === mod.id ? null : mod.id)}
                      >
                        <div className="flex items-start gap-3 mb-3">
                          <div className="relative shrink-0">
                            <img src={mod.photoUrl} alt={mod.name} className="w-14 h-14 rounded-full object-cover" />
                            {mod.isOnline && (
                              <span className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-emerald-500 border-2 border-background-50"></span>
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5">
                              <p className="text-sm font-bold text-foreground-950">{mod.name}</p>
                              {mod.isVerified && <i className="ri-verified-badge-fill text-[10px] text-sky-500"></i>}
                            </div>
                            <p className="text-[10px] text-foreground-500">{mod.username}</p>
                            <span className={`inline-block mt-1 text-[9px] px-1.5 py-0.5 rounded-full font-medium border ${roleColors.badge}`}>
                              {moderatorRoleLabels[mod.role]}
                            </span>
                          </div>
                        </div>

                        <p className="text-[10px] text-foreground-500 mb-2">
                          <i className="ri-map-pin-line text-[9px] mr-0.5"></i>{mod.assignedEntity}
                          <span className="mx-1.5">·</span>
                          Desde {mod.sinceDate}
                        </p>

                        <div className="flex items-center gap-4 text-[10px] text-foreground-500 mb-3">
                          <span><i className="ri-flashlight-line text-[9px] mr-0.5"></i>{(mod.totalActions / 1000).toFixed(1)}K</span>
                          <span><i className="ri-star-line text-[9px] mr-0.5"></i>Nivel {mod.level}</span>
                          <span><i className="ri-calendar-line text-[9px] mr-0.5"></i>{mod.sinceDate.split('-')[0]}</span>
                        </div>

                        {selectedMod === mod.id && (
                          <div className="pt-3 border-t border-background-200/50 animate-fadeIn">
                            <p className="text-[10px] font-bold text-foreground-400 uppercase tracking-wider mb-2">Desglose de Acciones</p>
                            <div className="grid grid-cols-3 gap-1.5">
                              {Object.entries(mod.stats).map(([key, val]) => {
                                const actionKey = key.replace(/s$/, '') as ModAction | 'reportado';
                                const fixedKey = actionKey === 'reportado' ? 'reportar' : actionKey as ModAction;
                                const config = modActionConfig[fixedKey];
                                return (
                                  <div key={key} className="text-center p-2 rounded-lg bg-background-100">
                                    <div className={`w-6 h-6 rounded-full ${config.bgColor} flex items-center justify-center mx-auto mb-1`}>
                                      <i className={`${config.icon} text-[9px] ${config.color}`}></i>
                                    </div>
                                    <p className="text-xs font-extrabold text-foreground-950">{(val / 1000).toFixed(1)}K</p>
                                    <p className="text-[8px] text-foreground-400">{config.label}</p>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* VIEW: ACCIONES */}
            {activeView === 'acciones' && (
              <div className="space-y-5">
                <div className="flex flex-wrap items-center gap-1.5">
                  {([{ id: 'todas' as const, label: 'Todas' }] as { id: ModAction | 'todas'; label: string }[]).concat(
                    Object.entries(modActionConfig).map(([key, config]) => ({
                      id: key as ModAction,
                      label: config.label,
                    }))
                  ).map((f) => (
                    <button
                      key={f.id}
                      onClick={() => setActionFilter(f.id)}
                      className={`px-3 py-1.5 rounded-full text-[11px] font-medium transition-all duration-200 whitespace-nowrap cursor-pointer border ${
                        actionFilter === f.id
                          ? 'bg-primary-500 text-white border-primary-500'
                          : 'bg-background-50 text-foreground-500 border-background-200 hover:border-foreground-300'
                      }`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>

                <div className="space-y-3">
                  {filteredActions.map((act) => (
                    <ActionCard key={act.id} action={act} />
                  ))}
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 mt-6">
                  {actionStats.map((s) => {
                    const config = modActionConfig[s.action];
                    const pct = totalActionsAll > 0 ? ((s.count / totalActionsAll) * 100).toFixed(1) : '0';
                    return (
                      <div key={s.action} className="p-3 rounded-xl bg-background-50 border border-background-200/70 text-center">
                        <div className={`w-8 h-8 rounded-full ${config.bgColor} flex items-center justify-center mx-auto mb-1.5`}>
                          <i className={`${config.icon} text-xs ${config.color}`}></i>
                        </div>
                        <p className="text-sm font-extrabold text-foreground-950">{(s.count / 1000).toFixed(1)}K</p>
                        <p className="text-[9px] text-foreground-500">{config.label} ({pct}%)</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* VIEW: REPORTES */}
            {activeView === 'reportes' && (
              <div className="space-y-5">
                <div className="flex flex-wrap items-center gap-1.5">
                  {([
                    { id: 'todas' as const, label: 'Todos' },
                    { id: 'pendiente' as const, label: 'Pendientes' },
                    { id: 'revisado' as const, label: 'Revisados' },
                    { id: 'resuelto' as const, label: 'Resueltos' },
                    { id: 'rechazado' as const, label: 'Rechazados' },
                  ]).map((f) => (
                    <button
                      key={f.id}
                      onClick={() => setReportFilter(f.id)}
                      className={`px-3 py-1.5 rounded-full text-[11px] font-medium transition-all duration-200 whitespace-nowrap cursor-pointer border ${
                        reportFilter === f.id
                          ? 'bg-primary-500 text-white border-primary-500'
                          : 'bg-background-50 text-foreground-500 border-background-200 hover:border-foreground-300'
                      }`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>

                <div className="space-y-3">
                  {filteredReports.map((report) => (
                    <div key={report.id} className="p-4 rounded-xl bg-background-50 border border-background-200/70">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <img src={report.assignedModeratorPhoto} alt={report.assignedModerator} className="w-8 h-8 rounded-full object-cover" />
                          <div>
                            <p className="text-xs font-bold text-foreground-950">{report.assignedModerator}</p>
                            <p className="text-[10px] text-foreground-500">{report.timestampRelative}</p>
                          </div>
                        </div>
                        <span className={`shrink-0 text-[9px] px-2 py-0.5 rounded-full font-medium ${
                          report.status === 'pendiente' ? 'bg-amber-100 text-amber-700' :
                          report.status === 'resuelto' ? 'bg-emerald-100 text-emerald-700' :
                          report.status === 'revisado' ? 'bg-sky-100 text-sky-700' :
                          'bg-slate-100 text-slate-600'
                        }`}>
                          {report.statusLabel}
                        </span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-[11px]">
                        <div>
                          <span className="text-foreground-400">Reportado por: </span>
                          <span className="font-medium text-foreground-700">{report.reportedByUsername}</span>
                        </div>
                        <div>
                          <span className="text-foreground-400">Objetivo: </span>
                          <span className="font-medium text-foreground-700">{report.reportedTargetUsername}</span>
                        </div>
                        <div>
                          <span className="text-foreground-400">Tipo: </span>
                          <span className="font-medium text-foreground-700">{report.targetType}</span>
                        </div>
                      </div>
                      <p className="text-[11px] text-foreground-600 mt-2 mb-1">{report.reason}</p>
                      {report.resolution && (
                        <p className="text-[10px] text-emerald-600 bg-emerald-50 px-2 py-1 rounded-lg inline-block">
                          <i className="ri-check-line text-[9px] mr-0.5"></i>{report.resolution}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* VIEW: BLOQUEOS */}
            {activeView === 'bloqueos' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-extrabold text-foreground-950 flex items-center gap-2">
                    <i className="ri-forbid-line text-red-500"></i>
                    Usuarios Bloqueados
                    <span className="text-[11px] text-foreground-400 font-normal">({bannedUsers.length})</span>
                  </h3>
                  <div className="flex gap-2 text-[10px]">
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500"></span>Permanentes</span>
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500"></span>Temporales</span>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {bannedUsers.map((b) => (
                    <div key={b.id} className="p-3.5 rounded-xl bg-background-50 border border-background-200/70">
                      <div className="flex items-center gap-3 mb-2">
                        <img src={b.photoUrl} alt={b.name} className="w-10 h-10 rounded-full object-cover opacity-60 shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-bold text-foreground-950 line-clamp-1">{b.username}</p>
                          <p className="text-[10px] text-foreground-500">Bloqueado por {b.bannedByUsername} · {b.bannedDateRelative}</p>
                        </div>
                        <span className={`shrink-0 text-[9px] px-2 py-0.5 rounded-full font-medium ${
                          b.isPermanent ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                        }`}>
                          {b.duration}
                        </span>
                      </div>
                      <p className="text-[10px] text-foreground-600 pl-[52px]">{b.reason}</p>
                      <p className="text-[9px] text-foreground-400 pl-[52px] mt-1">
                        Expira: {b.expiresDate}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* VIEW: AUDITORÍA */}
            {activeView === 'auditoria' && (
              <div className="space-y-5">
                <div className="flex flex-wrap items-center gap-1.5">
                  {([{ id: 'todas' as const, label: 'Todas las Acciones' }] as { id: ModAction | 'todas'; label: string }[]).concat(
                    Object.entries(modActionConfig).map(([key, config]) => ({
                      id: key as ModAction,
                      label: config.label,
                    }))
                  ).map((f) => (
                    <button
                      key={f.id}
                      onClick={() => setAuditActionFilter(f.id)}
                      className={`px-3 py-1.5 rounded-full text-[11px] font-medium transition-all duration-200 whitespace-nowrap cursor-pointer border ${
                        auditActionFilter === f.id
                          ? 'bg-primary-500 text-white border-primary-500'
                          : 'bg-background-50 text-foreground-500 border-background-200 hover:border-foreground-300'
                      }`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>

                <div className="space-y-2">
                  {filteredAudit.map((entry) => (
                    <AuditEntryCard key={entry.id} entry={entry} />
                  ))}
                </div>

                <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200/50">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-bold text-foreground-950 flex items-center gap-1.5">
                      <i className="ri-file-search-line text-slate-500"></i>
                      Resumen de Auditoría
                    </p>
                    <span className="text-[10px] text-foreground-500">{moderatorDashboardStats.totalAuditEntries.toLocaleString()} entradas totales</span>
                  </div>
                  <p className="text-[11px] text-foreground-600">
                    El registro de auditoría es inmutable. Cada entrada contiene: moderador responsable, rol, acción ejecutada, tipo de objetivo, estado anterior y posterior, motivo, hash de IP y marca de tiempo. Esta información solo es accesible para Super Admins y está protegida contra modificaciones.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* RULES SECTION */}
        <section className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
          {moderatorRules.map((rule) => (
            <div key={rule.id} className={`p-5 rounded-2xl ${rule.bgColor} border border-background-200/50 text-center`}>
              <div className={`w-10 h-10 rounded-full bg-background-50 flex items-center justify-center mx-auto mb-2`}>
                <i className={`${rule.icon} text-lg ${rule.color}`}></i>
              </div>
              <p className="text-sm font-bold text-foreground-950 mb-1">{rule.title}</p>
              <p className="text-[11px] text-foreground-500">{rule.description}</p>
            </div>
          ))}
        </section>

        {/* CROSS LINKS */}
        <section className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6 mb-6">
          {[
            { to: '/centro-social', icon: 'ri-pulse-line', iconBg: 'bg-rose-100', iconColor: 'text-rose-600', label: 'Centro Social', desc: 'Tendencias de la comunidad' },
            { to: '/seguir-usuarios', icon: 'ri-group-line', iconBg: 'bg-amber-100', iconColor: 'text-amber-600', label: 'Seguir Usuarios', desc: 'Red social entre personas' },
            { to: '/mensajes-privados', icon: 'ri-message-2-line', iconBg: 'bg-violet-100', iconColor: 'text-violet-600', label: 'Mensajes Privados', desc: 'Comunicación directa' },
            { to: '/centro-notificaciones', icon: 'ri-notification-3-line', iconBg: 'bg-sky-100', iconColor: 'text-sky-600', label: 'Notificaciones', desc: 'Alertas y actividad' },
          ].map((link, i) => (
            <Link key={i} to={link.to} className="p-4 rounded-xl bg-background-100 border border-background-200/70 hover:border-rose-300/60 transition-all duration-200 text-center">
              <div className={`w-10 h-10 rounded-full ${link.iconBg} flex items-center justify-center mx-auto mb-2`}>
                <i className={`${link.icon} text-base ${link.iconColor}`}></i>
              </div>
              <p className="text-sm font-semibold text-foreground-900">{link.label}</p>
              <p className="text-[10px] text-foreground-500 mt-1">{link.desc}</p>
            </Link>
          ))}
        </section>

        {/* REGLA FINAL */}
        <section className="p-6 rounded-2xl bg-rose-100/70 border border-rose-200/50 text-center">
          <div className="w-12 h-12 rounded-full bg-primary-500 flex items-center justify-center mx-auto mb-3">
            <i className="ri-file-search-line text-xl text-white"></i>
          </div>
          <p className="text-lg font-extrabold text-foreground-950 mb-2">Todo debe quedar auditado.</p>
          <p className="text-sm text-foreground-600 max-w-xl mx-auto">
            Cada acción — cada mensaje ocultado, cada usuario bloqueado, cada contenido destacado — queda registrada para siempre en el sistema de auditoría. Moderador responsable, fecha y hora exacta, motivo detallado, estado anterior y posterior. La transparencia es la base de la confianza, y en RFW nos tomamos la confianza de la comunidad muy en serio. Porque una comunidad sana no se construye con secretos, se construye con claridad.
          </p>
        </section>
      </main>
    </div>
  );
}

function ActionCard({ action }: { action: ModeratorActionRecord }) {
  const config = modActionConfig[action.action];
  return (
    <div className="flex items-start gap-3 p-3.5 rounded-xl bg-background-50 border border-background-200/70">
      <div className="relative shrink-0">
        <img src={action.moderatorPhoto} alt={action.moderatorName} className="w-11 h-11 rounded-full object-cover" />
        <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full ${config.bgColor} flex items-center justify-center border-2 border-background-50`}>
          <i className={`${config.icon} text-[8px] ${config.color}`}></i>
        </div>
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 mb-0.5">
          <p className="text-xs font-bold text-foreground-950">{action.moderatorName}</p>
          <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[8px] font-semibold ${config.bgColor} ${config.color} border border-current/20`}>
            <i className={`${config.icon} text-[7px]`}></i>
            {config.label}
          </span>
          <span className="text-[10px] text-foreground-400 ml-auto shrink-0">{action.timestampRelative}</span>
        </div>
        <p className="text-[11px] text-foreground-600 mb-1">
          <span className="font-semibold">{action.targetType}</span> de <span className="font-medium text-foreground-900">{action.targetUsername}</span>
        </p>
        <p className="text-[10px] text-foreground-500 mb-1 line-clamp-1">{action.reason}</p>
        <p className="text-[9px] text-foreground-400">
          <i className="ri-map-pin-line text-[8px] mr-0.5"></i>{action.context}
        </p>
      </div>
    </div>
  );
}

function AuditEntryCard({ entry }: { entry: AuditLogEntry }) {
  const config = modActionConfig[entry.action];
  const roleColors = moderatorRoleColors[entry.moderatorRole];
  return (
    <div className="p-3.5 rounded-xl bg-background-50 border border-background-200/70">
      <div className="flex items-start gap-3 mb-2">
        <div className="relative shrink-0">
          <img src={entry.moderatorPhoto} alt={entry.moderatorName} className="w-9 h-9 rounded-full object-cover" />
          <div className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full ${config.bgColor} flex items-center justify-center border-2 border-background-50`}>
            <i className={`${config.icon} text-[7px] ${config.color}`}></i>
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 mb-0.5">
            <p className="text-xs font-bold text-foreground-950">{entry.moderatorName}</p>
            <span className={`text-[8px] px-1 py-0.5 rounded-full font-medium border ${roleColors.badge}`}>
              {moderatorRoleLabels[entry.moderatorRole]}
            </span>
            <span className="text-[10px] text-foreground-400 ml-auto shrink-0">{entry.timestampRelative}</span>
          </div>
          <p className="text-[11px] text-foreground-600">
            <span className={`inline-flex items-center gap-0.5 font-semibold ${config.color}`}>
              <i className={`${config.icon} text-[9px]`}></i>{config.label}
            </span>
            {' '}· {entry.targetType} {entry.targetName}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-[10px] pl-[48px]">
        <div>
          <span className="text-foreground-400">Anterior: </span>
          <span className="text-foreground-600">{entry.previousState}</span>
        </div>
        <div>
          <span className="text-foreground-400">Nuevo: </span>
          <span className="text-foreground-600">{entry.newState}</span>
        </div>
      </div>

      <div className="flex items-center gap-4 text-[9px] text-foreground-400 mt-1.5 pl-[48px]">
        <span>IP: {entry.ipHash}</span>
        <span>Reversible: {entry.reversible ? <span className="text-amber-600">Sí</span> : <span className="text-red-600">No</span>}</span>
        {entry.reverted && <span className="text-emerald-600 font-semibold">· Revertido</span>}
      </div>
    </div>
  );
}